<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  apiKey: { type: String, required: true },
});

const emit = defineEmits(['select']);

const { t } = useI18n();

const search = ref('');
const gifs = ref([]);
const loading = ref(false);
let debounceTimer = null;

async function fetchGifs(query) {
  loading.value = true;
  try {
    const base = 'https://api.giphy.com/v1/gifs';
    const endpoint = query ? 'search' : 'trending';
    const params = new URLSearchParams({
      api_key: props.apiKey,
      limit: 20,
      rating: 'g',
      ...(query ? { q: query } : {}),
    });
    const res = await fetch(`${base}/${endpoint}?${params}`);
    const json = await res.json();
    gifs.value = json.data ?? [];
  } finally {
    loading.value = false;
  }
}

function onInput() {
  clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => fetchGifs(search.value), 400);
}

onMounted(() => fetchGifs(''));
</script>

<template>
  <div class="flex flex-col h-full">
    <div class="p-2 pb-1">
      <input
        v-model="search"
        type="text"
        class="!mb-0 !h-8 !text-sm !px-4 w-full"
        :placeholder="t('GIF.PLACEHOLDER')"
        @input="onInput"
      />
    </div>
    <div class="flex-1 overflow-y-auto px-1 py-1">
      <div
        v-if="loading"
        class="flex items-center justify-center h-full text-n-slate-11 text-sm"
      >
        {{ t('GIF.LOADING') }}
      </div>
      <div
        v-else-if="gifs.length === 0"
        class="flex items-center justify-center h-full text-n-slate-11 text-sm"
      >
        {{ t('GIF.NOT_FOUND') }}
      </div>
      <div v-else class="columns-2 gap-1">
        <button
          v-for="gif in gifs"
          :key="gif.id"
          class="block w-full mb-1 rounded overflow-hidden cursor-pointer bg-transparent border-0 p-0 hover:opacity-75 transition-opacity"
          @click="
            emit('select', {
              url: gif.images.original.url,
              mp4: gif.images.original.mp4,
            })
          "
        >
          <img
            :src="gif.images.fixed_width_small.url"
            :alt="gif.title"
            class="w-full h-auto block"
            loading="lazy"
          />
        </button>
      </div>
    </div>
  </div>
</template>
